import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {RootState} from '../../../hooks/store';
import {AvailabilityApiResponseVenue} from '../../../../server/routes/availability/types';
import {composeFetchVenueInformation} from '../../../api/venue';
import {Status} from '../availability/slice';

export type VenueState = {
  status: Status;
  data: AvailabilityApiResponseVenue;
};

const initialState: VenueState = {
  status: 'idle',
  data: {
    name: '',
    address: {
      addressLines: [],
      postalCode: '',
    },
    openingHours: [],
    uri: '',
    id: null,
    type: {
      name: '',
      pluralName: '',
    },
  },
};

const isRequestPending = (state: RootState) => {
  const status = selectVenueRequestStatus(state);
  return status !== 'pending';
};

export const fetchVenueInformation = createAsyncThunk(
  'venue/fetch',
  ({venueId}: {venueId: string}) => {
    const getVenueInformation = composeFetchVenueInformation();
    return getVenueInformation({venueId});
  },
  {
    condition(_, {getState}) {
      const state = getState() as RootState;
      return isRequestPending(state);
    },
  },
);

export const venueSlice = createSlice({
  name: 'venue',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(fetchVenueInformation.fulfilled, (state, action) => {
      state.data.name = action.payload.name;
      state.data.uri = action.payload.uri.desktopUri;
      state.data.id = action.payload.id;
      state.data.address.addressLines =
        action.payload.location.address.addressLines;
      state.data.address.postalCode =
        action.payload.location.address.postalCode;
      state.data.openingHours = action.payload.openingHours || [];
      state.data.type = {
        name: action.payload.type.name,
        pluralName: action.payload.type.pluralName,
      };
      state.status = 'fulfilled';
    });
    builder.addCase(fetchVenueInformation.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(fetchVenueInformation.rejected, (state) => {
      state.status = 'rejected';
    });
  },
});

const selectVenueRootState = (state: RootState) => state.venue;

export const selectVenueRequestStatus = (state: RootState) =>
  selectVenueRootState(state).status;

export const selectVenueData = (state: RootState) =>
  selectVenueRootState(state).data;

export const selectVenueName = (state: RootState) =>
  selectVenueData(state).name;

export const selectVenueId = (state: RootState) => selectVenueData(state).id;
